import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { BellRing, Share, Smartphone, X } from 'lucide-react'
import { Button, Chip, Banner, useToast } from '../ds/index'
import { Icon } from './Icon'
import { api, post, del } from '../lib/api'
import { ago } from '../lib/format'

type Device = { id: string; device: string | null; created_at: string; last_sent_at: string | null; last_error: string | null; endpoint: string }
type State = 'checking' | 'unsupported' | 'install_first' | 'blocked' | 'off' | 'on'

export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
export const isInstalled = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true
const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

function keyBytes(base64url: string) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4)
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

export function registerServiceWorker() {
  if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => { /* alerts just stay off */ })
}

/**
 * Turn lead alerts on for this phone. `compact` is the prompt on My leads;
 * the full version lives in Settings and lists every device. A business's
 * portal uses it too, on its own routes (`base`), under its own app name, with
 * `beforeInstall` shown ahead of the iPhone home-screen steps (a password: the
 * home-screen app does not share Safari's sign-in) and `onChange` when alerts
 * go on or off.
 */
const REP_WORDS = {
  off: 'Get a notification on this device the moment a lead is routed to you. The first reply usually wins.',
  on: 'New leads buzz here within seconds of being routed to you.',
}
export function PhoneAlerts({ compact = false, base = '/api/push', appName = 'Leads', words = REP_WORDS, beforeInstall, onChange }: {
  compact?: boolean; base?: string; appName?: string; words?: { off: string; on: string }; beforeInstall?: ReactNode; onChange?: () => void
}) {
  const [state, setState] = useState<State>('checking')
  const [devices, setDevices] = useState<Device[]>([])
  const [busy, setBusy] = useState(false)
  const [current, setCurrent] = useState<string | null>(null)
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem('alerts-prompt-hidden') === '1' } catch { return false } })
  const toast = useToast()

  const check = useCallback(async () => {
    let list: Device[] = []
    try { list = (await api<{ devices: Device[] }>(base)).devices; setDevices(list) } catch { /* shown as off */ }
    if (!supported()) { setState(isIos() && !isInstalled() ? 'install_first' : 'unsupported'); return }
    if (Notification.permission === 'denied') { setState('blocked'); return }
    const reg = await navigator.serviceWorker.getRegistration()
    const sub = reg ? await reg.pushManager.getSubscription() : null
    setCurrent(sub?.endpoint || null)
    setState(sub && list.some((d) => d.endpoint === sub.endpoint) ? 'on' : 'off')
  }, [base])
  useEffect(() => { void check() }, [check])

  const turnOn = async () => {
    setBusy(true)
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') { setState(permission === 'denied' ? 'blocked' : 'off'); return }
      const { public_key } = await api<{ public_key: string }>(base)
      let sub = await reg.pushManager.getSubscription()
      if (sub && !devices.some((d) => d.endpoint === sub!.endpoint)) { await sub.unsubscribe(); sub = null }
      sub = sub || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(public_key) })
      await post(`${base}/subscribe`, sub.toJSON())
      toast('Alerts are on for this device', 'pos')
      await check()
      onChange?.()
    } catch (e) {
      toast((e as Error).message || 'Alerts could not be turned on', 'neg')
    } finally { setBusy(false) }
  }
  const turnOff = async (d: Device) => {
    setBusy(true)
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      const sub = reg ? await reg.pushManager.getSubscription() : null
      if (sub && sub.endpoint === d.endpoint) await sub.unsubscribe()
      await del(`${base}/devices/${d.id}`)
      toast('Alerts are off for that device', 'pos')
      await check()
      onChange?.()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const sendTest = async () => {
    setBusy(true)
    try {
      const r = await post<{ sent: number; failed: number; removed: number }>(`${base}/test`, {})
      toast(r.sent ? `Test sent to ${r.sent} device${r.sent === 1 ? '' : 's'}. It should buzz in a few seconds.` : 'The test did not go through. Try turning alerts off and on.', r.sent ? 'pos' : 'neg')
      await check()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  if (state === 'checking') return null

  const installSteps = (
    <>
      {beforeInstall}
      <ol className="alert-steps">
        <li>Tap <Icon of={Share} /> <strong>Share</strong> at the bottom of Safari.</li>
        <li>Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
        <li>Open <strong>{appName}</strong> from your home screen, sign in, and turn alerts on there.</li>
      </ol>
    </>
  )

  if (compact) {
    if (state === 'on' || hidden) return null
    const hide = () => { setHidden(true); try { localStorage.setItem('alerts-prompt-hidden', '1') } catch { /* fine */ } }
    return (
      <div className="alert-prompt">
        <span className="alert-prompt-icon"><Icon of={BellRing} /></span>
        <div className="alert-prompt-body">
          <strong>Get a buzz the moment a lead comes in</strong>
          {state === 'off' && <span className="muted">The first reply usually wins. Alerts go to this phone only, for your leads only.</span>}
          {state === 'install_first' && <><span className="muted">On iPhone, alerts work once {appName} is on your home screen:</span>{installSteps}</>}
          {state === 'blocked' && <span className="muted">Alerts are blocked for this site. Allow notifications for it in your browser or phone settings, then come back.</span>}
          {state === 'unsupported' && <span className="muted">This browser cannot show alerts. On a phone, use Safari (iPhone) or Chrome (Android).</span>}
        </div>
        {state === 'off' && <Button onClick={turnOn} disabled={busy}>{busy ? 'Turning on' : 'Turn on alerts'}</Button>}
        <button className="alert-prompt-close" onClick={hide} aria-label="Hide this for now"><Icon of={X} /></button>
      </div>
    )
  }

  return (
    <div className="stack-tight">
      {state === 'on' && <Banner tone="pos" title="Alerts are on for this device">{words.on}</Banner>}
      {state === 'off' && <p className="mailbox-note" style={{ margin: 0 }}>{words.off}</p>}
      {state === 'install_first' && <><p className="mailbox-note" style={{ margin: 0 }}>On iPhone, alerts work once {appName} is on your home screen:</p>{installSteps}</>}
      {state === 'blocked' && <Banner tone="warn" title="Alerts are blocked for this site">Allow notifications for it in your browser or phone settings, then reload this page.</Banner>}
      {state === 'unsupported' && <Banner tone="warn" title="This browser cannot show alerts">On a phone, use Safari (iPhone, added to the home screen) or Chrome (Android).</Banner>}
      <div className="row-tight">
        {state === 'off' && <Button onClick={turnOn} disabled={busy}>{busy ? 'Turning on' : 'Turn on alerts here'}</Button>}
        {devices.length > 0 && <Button variant="secondary" onClick={sendTest} disabled={busy}>Send a test alert</Button>}
      </div>
      {devices.length > 0 && (
        <ul className="comms-list">
          {devices.map((d) => (
            <li key={d.id}>
              <Icon of={Smartphone} />
              <span className="comms-item">
                <span>{d.device || 'Browser'} {d.endpoint === current ? <Chip tone="pos">this device</Chip> : null}</span>
                <span className="muted">added {ago(d.created_at)}{d.last_sent_at ? `, last alert ${ago(d.last_sent_at)}` : ''}{d.last_error ? `. ${d.last_error}` : ''}</span>
              </span>
              <Button variant="ghost" onClick={() => turnOff(d)} disabled={busy}>Turn off</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
